import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import { groupingBasisOf, groupWords } from 'utils/groupLabel';

import styles from './AgeGroupDeletionImpactDialog.module.css';

// Age group deletion impact dialog
// (splitfast-portal docs/age-group-lifecycle-design.md §5.2, §7.3).
// Rendered from the 409 report returned by DELETE /Clubs/{id}/agegroups/{id}.
// Unlike the volunteer-role dialog, everything here is INFORMATIONAL: the
// listed meets keep working exactly as they are — nothing is cancelled or
// removed. The acknowledgment is about the one-way door: a deleted group can
// never be added to a program again, and if it is removed from one of these
// programs it can't be put back.

const plural = (count, singular, pluralWord) =>
  count === 1 ? singular : pluralWord || `${singular}s`;

const formatMeetDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const AgeGroupDeletionImpactDialog = ({
  isOpen,
  report,
  onCancel,
  onConfirm,
  deleting,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  // Age Group, or Year Group for a club that groups by year level.
  const { selectedClub } = useSelector((state) => state.user || {});
  const words = groupWords(groupingBasisOf(selectedClub));

  // A refreshed report (e.g. after a race-safety 409) resets the acknowledgment
  useEffect(() => {
    setAcknowledged(false);
  }, [report]);

  if (!isOpen || !report) return null;

  const affectedMeets = report.affectedMeets || [];
  const completedUsage = report.completedMeetUsage || {};

  const canDelete = affectedMeets.length === 0 || acknowledged;

  const handleConfirm = () => {
    if (!canDelete || deleting) return;
    onConfirm({
      acknowledgedMeetIds: affectedMeets.map((meet) => meet.meetId),
    });
  };

  return (
    <CustomNewConfirm
      isOpen={isOpen}
      onClose={onCancel}
      onCancel={onCancel}
      title={`Delete ${words.group} — ${report.ageGroupName}`}
      description={`This ${words.lower.group} is used in the programs of meets that haven't been completed. Those meets keep working exactly as they are — but once the group is deleted it can't be added to any program again, and if it's removed from one of these programs it can't be put back.`}
      confirmButtonText={deleting ? 'Deleting...' : `Delete ${words.group}`}
      confirmButtonVariant="danger"
      onConfirm={handleConfirm}
      isLoading={deleting || !canDelete}
    >
      <div className={styles.sections}>
        {affectedMeets.length > 0 && (
          <div className={styles.section}>
            <div className={`${styles.sectionTitle} ${styles.info}`}>
              Used by these meets — unchanged by the delete
              <span className={styles.count}>
                {affectedMeets.length} {plural(affectedMeets.length, 'meet')}
              </span>
            </div>
            <ul className={styles.itemList}>
              {affectedMeets.map((meet) => (
                <li key={meet.meetId}>
                  {meet.meetName} — {formatMeetDate(meet.meetDate)}
                  {meet.meetStatus ? ` (${meet.meetStatus})` : ''}
                </li>
              ))}
            </ul>
            <label className={styles.ackRow}>
              <input
                type="checkbox"
                className={styles.ackCheckbox}
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span>
                Delete the group anyway — I understand it can&apos;t be added
                back to these programs
              </span>
            </label>
            {!acknowledged && (
              <div className={styles.ackHint}>
                Delete {words.group} stays disabled until you confirm. The group
                also disappears from the pickers for new meet programs and start
                lists.
              </div>
            )}
          </div>
        )}

        {completedUsage.meetCount > 0 && (
          <div className={styles.section}>
            <div className={`${styles.sectionTitle} ${styles.info}`}>
              Completed meets — history kept
            </div>
            <div className={styles.historyNote}>
              Also used in {completedUsage.meetCount}{' '}
              {plural(completedUsage.meetCount, 'completed meet')}; their
              records are unaffected.
            </div>
          </div>
        )}
      </div>
    </CustomNewConfirm>
  );
};

AgeGroupDeletionImpactDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  report: PropTypes.shape({
    errorCode: PropTypes.string,
    ageGroupId: PropTypes.string,
    ageGroupName: PropTypes.string,
    affectedMeets: PropTypes.array,
    completedMeetUsage: PropTypes.shape({
      meetCount: PropTypes.number,
    }),
  }),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  deleting: PropTypes.bool,
};

export default AgeGroupDeletionImpactDialog;
